/** Session analytics scope passed to the advisor API for follow-up retention. */

/** Dashboard id embedded in routes like /console/DashboardData/6208 */
export const dashboardIdFromRouteSubPage = (subPage) => {
  if (subPage == null || String(subPage).trim() === "") return null;
  const raw = String(subPage).trim();
  if (!/^\d+$/.test(raw)) return null;
  const id = parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
};

/**
 * Dashboard id sent to the advisor API.
 * Fresh advisor chats must NOT inherit a stale Redux dashboard from a prior visit.
 */
export const resolveContextDashboardId = ({
  context,
  sessionAnalyticsScope,
  isAdvisorPage = false,
}) => {
  const ctx = context && typeof context === "object" ? context : {};
  if (sessionAnalyticsScope?.dashboardId != null) {
    return sessionAnalyticsScope.dashboardId;
  }
  const fromUrl = dashboardIdFromRouteSubPage(ctx.subPage);
  if (fromUrl != null) {
    return fromUrl;
  }
  if (isAdvisorPage) {
    return null;
  }
  if (ctx.pageType !== "dataFilter") {
    return null;
  }
  const raw = ctx.dashboardId;
  if (raw == null || raw === "") return null;
  const id = parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
};

/** Latest explicit dashboard pick from chat messages (picker selection). */
export const deriveAnalyticsScopeFromMessages = (messages) => {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const sel = messages[i]?.dashboardSelection || messages[i]?.dashboard_selection;
    if (sel?.id == null) continue;
    const id = parseInt(sel.id, 10);
    if (Number.isNaN(id)) continue;
    return {
      dashboardId: id,
      orgWide: false,
      days: null,
      since: null,
      until: null,
    };
  }
  return null;
};

/** Latest dashboard pick object for API page_context.last_dashboard_selection. */
export const lastDashboardSelectionFromMessages = (messages) => {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const sel = messages[i]?.dashboardSelection || messages[i]?.dashboard_selection;
    if (sel?.id == null) continue;
    const id = parseInt(sel.id, 10);
    if (Number.isNaN(id)) continue;
    return {
      id,
      ...(typeof sel.name === "string" && sel.name.trim()
        ? { name: sel.name.trim() }
        : {}),
    };
  }
  return null;
};

const parseLockedDashboardId = (...candidates) => {
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const id = parseInt(raw, 10);
    if (!Number.isNaN(id)) return id;
  }
  return null;
};

const parseDaysFromAssistantText = (text) => {
  const daysMatch =
    text.match(/son\s+(\d+)\s+g/i) || text.match(/last\s+(\d+)\s+days?/i);
  if (!daysMatch) return null;
  const days = parseInt(daysMatch[1], 10);
  return Number.isNaN(days) ? null : days;
};

/**
 * Merge inferred scope with prior session scope.
 * Never downgrade an established dashboard pick to org-wide.
 */
export const mergeSessionAnalyticsScope = (priorScope, inferredScope, effectiveContext) => {
  const ctx = effectiveContext && typeof effectiveContext === "object" ? effectiveContext : {};
  const lockedId = parseLockedDashboardId(
    priorScope?.dashboardId,
    ctx.analyticsScope?.dashboardId,
    ctx.hasDashboard ? ctx.dashboardId : null
  );

  if (lockedId != null) {
    return {
      dashboardId: lockedId,
      orgWide: false,
      days: inferredScope?.days ?? priorScope?.days ?? ctx.analyticsScope?.days ?? null,
      since:
        inferredScope?.since ??
        priorScope?.since ??
        ctx.analyticsScope?.since ??
        ctx.since ??
        null,
      until:
        inferredScope?.until ??
        priorScope?.until ??
        ctx.analyticsScope?.until ??
        ctx.until ??
        null,
    };
  }

  if (inferredScope && typeof inferredScope === "object") {
    return inferredScope;
  }
  if (priorScope && typeof priorScope === "object") {
    return priorScope;
  }
  return null;
};

export const deriveSessionAnalyticsScope = (
  effectiveContext,
  assistantContent,
  priorScope = null
) => {
  const ctx = effectiveContext && typeof effectiveContext === "object" ? effectiveContext : {};
  const text = typeof assistantContent === "string" ? assistantContent.trim() : "";

  const lockedId = parseLockedDashboardId(
    priorScope?.dashboardId,
    ctx.analyticsScope?.dashboardId,
    ctx.hasDashboard ? ctx.dashboardId : null
  );
  if (lockedId != null) {
    const daysFromText = parseDaysFromAssistantText(text);
    return {
      dashboardId: lockedId,
      orgWide: false,
      days: daysFromText ?? priorScope?.days ?? ctx.analyticsScope?.days ?? null,
      since:
        priorScope?.since ??
        ctx.analyticsScope?.since ??
        ctx.since ??
        null,
      until:
        priorScope?.until ??
        ctx.analyticsScope?.until ??
        ctx.until ??
        null,
    };
  }

  if (!text) return null;
  const hasMetrics =
    (/\d/.test(text) && text.includes("%")) ||
    /\byorum\b/i.test(text) ||
    /\breview/i.test(text);
  if (!hasMetrics) return null;

  const days = parseDaysFromAssistantText(text) ?? 7;

  return {
    dashboardId: null,
    orgWide: true,
    days,
    since: ctx.since || null,
    until: ctx.until || null,
  };
};

const padDatePart = (n) => String(n).padStart(2, "0");

const formatLocalYmd = (dt) =>
  `${dt.getFullYear()}-${padDatePart(dt.getMonth() + 1)}-${padDatePart(dt.getDate())}`;

/** Inclusive local-calendar lookback (chip 7 = today and 6 days before). */
export const lookbackDateWindow = (days, now = new Date()) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return null;
  const until = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const since = new Date(until);
  since.setDate(since.getDate() - (n - 1));
  return { days: n, since: formatLocalYmd(since), until: formatLocalYmd(until) };
};

/** Prefer a chip window whose span matches days; otherwise recompute from today. */
export const chipPeriodWindow = (scope, now = new Date()) => {
  if (!scope || scope.days == null) return null;
  const win = lookbackDateWindow(scope.days, now);
  if (!win) return null;
  if (scope.since && scope.until) {
    const start = new Date(`${scope.since}T00:00:00`);
    const end = new Date(`${scope.until}T00:00:00`);
    const span = Math.round((end - start) / 86400000) + 1;
    if (span === win.days) {
      return { days: win.days, since: scope.since, until: scope.until };
    }
  }
  return win;
};

export const analyticsScopeForApi = (scope) => {
  if (!scope || typeof scope !== "object") return null;
  if (scope.dashboardId != null) {
    return {
      dashboardId: scope.dashboardId,
      orgWide: false,
      days: scope.days ?? null,
      since: scope.since ?? null,
      until: scope.until ?? null,
    };
  }
  if (scope.orgWide) {
    return {
      dashboardId: null,
      orgWide: true,
      days: scope.days ?? 7,
      since: scope.since ?? null,
      until: scope.until ?? null,
    };
  }
  return null;
};
